import {
  askOfferNotaComercialForContact,
  applyOfferedNotaComercial,
  SIDE_CLIENT,
  SIDE_SUPPLIER,
} from "@/modules/nota_comercial_module/utils/offerNotaComercialOnOperationCreate";

const PENDING_STORAGE_KEY = "ncOfferPendingOperation";
/** La elección sobrevive al alta, no a la sesión entera. */
const PENDING_TTL_MS = 30 * 60 * 1000;

/**
 * Qué contacto dispara la oferta en cada modelo, y de qué lado son sus notas.
 * La clave es el nombre del modelo dinámico en minúsculas.
 */
const OFFER_BY_MODEL = {
  proform: { contactField: "client", side: SIDE_CLIENT },
  shipping: { contactField: "shipper", side: SIDE_SUPPLIER },
};

/**
 * Oferta de notas comerciales al elegir el contacto de una operación: el cliente de
 * una Proforma y el proveedor (`shipper`) de un embarque. Corre tanto en el alta como
 * al cambiar el contacto de un registro existente, pero no al abrirlo.
 * Usar en dynamic-model-edit y DynamicModelCustomFormShell.
 *
 * Nota: con visualGroups, FormV2 guarda el valor en `group_<nombre>.<campo>`.
 * La aplicación vive en la nota (`notacomercial.applied_to`), no en la operación.
 */
export default {
  data() {
    return {
      ncOfferPromptedContactId: "",
      ncOfferLastSeenContactId: "",
      ncOfferPendingChoice: null,
      ncOfferAppliedTargetId: "",
      ncOfferInFlight: false,
      ncOfferBaselineTaken: false,
      ncOfferClientUnwatch: null,
      ncOfferApplyTimer: null,
    };
  },
  created() {
    this.restoreNcOfferPendingFromStorage();
    this.ncOfferClientUnwatch = this.$watch(
      () => this.getOfferContactId(),
      (contactKey) => {
        if (contactKey) this.maybeAskNotaComercialOnContactSelect();
      }
    );
  },
  beforeDestroy() {
    if (typeof this.ncOfferClientUnwatch === "function") {
      this.ncOfferClientUnwatch();
      this.ncOfferClientUnwatch = null;
    }
    if (this.ncOfferApplyTimer) {
      clearTimeout(this.ncOfferApplyTimer);
      this.ncOfferApplyTimer = null;
    }
  },
  methods: {
    /** Config de oferta del modelo en pantalla, o null si no ofrece nada. */
    ncOfferConfig() {
      const modelName = String(this.routeModelName || this.selectedModel || "").toLowerCase();
      return OFFER_BY_MODEL[modelName] || null;
    },

    extractContactId(client) {
      if (client == null || client === "") return "";
      if (typeof client === "string" || typeof client === "number") {
        const s = String(client).trim();
        return s && s !== "[object Object]" ? s : "";
      }
      if (typeof client === "object") {
        if (client.$oid) return String(client.$oid);
        const raw = client._id != null ? client._id : client.id != null ? client.id : null;
        if (raw == null || raw === "") return "";
        if (typeof raw === "object") {
          if (raw.$oid) return String(raw.$oid);
          if (typeof raw.toHexString === "function") return raw.toHexString();
          if (raw._id != null || raw.id != null) return this.extractContactId(raw);
          const s = String(raw);
          return s && s !== "[object Object]" ? s : "";
        }
        return String(raw);
      }
      return "";
    },

    /**
     * Valor del campo de contacto, buscándolo primero en los buckets `group_*` de
     * visualGroups y recién después en la raíz.
     *
     * Ese orden importa: en edición la raíz conserva el valor con el que se cargó el
     * registro y la edición cae en el bucket, así que mirar la raíz primero devuelve
     * el contacto viejo. Es además el orden que usa el guardado, donde
     * flattenVisualGroupBuckets vuelca el bucket sobre la raíz.
     */
    getContactValueFromSource(source, contactField) {
      if (!source || typeof source !== "object" || !contactField) return null;
      for (const key of Object.keys(source)) {
        if (!String(key).startsWith("group_")) continue;
        const nested = source[key];
        if (
          nested &&
          typeof nested === "object" &&
          !Array.isArray(nested) &&
          nested[contactField] != null &&
          nested[contactField] !== ""
        ) {
          return nested[contactField];
        }
      }
      if (source[contactField] != null && source[contactField] !== "") {
        return source[contactField];
      }
      return null;
    },

    getOfferContactId(formOverride) {
      const config = this.ncOfferConfig();
      if (!config) return "";
      const { contactField } = config;

      const fromOverride = this.extractContactId(
        this.getContactValueFromSource(formOverride, contactField)
      );
      if (fromOverride) return fromOverride;

      const fromForm = this.extractContactId(
        this.getContactValueFromSource(this.form, contactField)
      );
      if (fromForm) return fromForm;

      const engine = this.$refs && (this.$refs.formV2 || this.$refs.customForm);
      if (engine && typeof engine.getState === "function") {
        return this.extractContactId(
          this.getContactValueFromSource(engine.getState(), contactField)
        );
      }
      return "";
    },

    /** Alta (todavía sin id) o edición de un registro existente. */
    isOfferAddMode() {
      if (typeof this.isAddMode === "boolean") return this.isAddMode;
      const path = String((this.$route && this.$route.path) || "");
      return /\/add\/?$/i.test(path);
    },

    persistNcOfferPending() {
      try {
        if (!this.ncOfferPendingChoice?.noteId) {
          sessionStorage.removeItem(PENDING_STORAGE_KEY);
          return;
        }
        sessionStorage.setItem(
          PENDING_STORAGE_KEY,
          JSON.stringify({ ...this.ncOfferPendingChoice, savedAt: Date.now() })
        );
      } catch (e) {
        /* sin sessionStorage la oferta sigue viva en memoria */
      }
    },

    clearNcOfferPendingStorage() {
      try {
        sessionStorage.removeItem(PENDING_STORAGE_KEY);
      } catch (e) {
        /* ignore */
      }
    },

    restoreNcOfferPendingFromStorage() {
      try {
        const raw = sessionStorage.getItem(PENDING_STORAGE_KEY);
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (
          parsed?.noteId &&
          Number(parsed.amount) > 0 &&
          Date.now() - Number(parsed.savedAt || 0) < PENDING_TTL_MS
        ) {
          this.ncOfferPendingChoice = {
            noteId: String(parsed.noteId),
            amount: Number(parsed.amount),
            side: parsed.side === SIDE_SUPPLIER ? SIDE_SUPPLIER : SIDE_CLIENT,
            contactId: parsed.contactId ? String(parsed.contactId) : "",
          };
        } else {
          sessionStorage.removeItem(PENDING_STORAGE_KEY);
        }
      } catch (e) {
        /* ignore */
      }
    },

    scheduleApplyPendingNotaComercialOffer(targetId) {
      const id = targetId != null ? String(targetId) : "";
      if (!id || !this.ncOfferPendingChoice) return;
      if (this.ncOfferApplyTimer) clearTimeout(this.ncOfferApplyTimer);
      // Dar tiempo a que el autosave persista el contacto en la operación.
      this.ncOfferApplyTimer = setTimeout(() => {
        this.ncOfferApplyTimer = null;
        this.applyPendingNotaComercialOffer(id);
      }, 600);
    },

    async maybeAskNotaComercialOnContactSelect(formOverride) {
      const config = this.ncOfferConfig();
      if (!config) return;
      if (this.ncOfferInFlight) return;

      const contactKey = this.getOfferContactId(formOverride);
      if (!contactKey) return;

      // Al abrir un registro que ya tiene contacto, la primera lectura no es una
      // elección del usuario: es el formulario cargándose. Se toma como punto de
      // partida y recién se ofrece cuando el contacto cambia.
      if (!this.ncOfferBaselineTaken) {
        this.ncOfferBaselineTaken = true;
        if (!this.isOfferAddMode()) {
          this.ncOfferLastSeenContactId = contactKey;
          this.ncOfferPromptedContactId = contactKey;
          return;
        }
      }

      if (this.ncOfferLastSeenContactId !== contactKey) {
        this.ncOfferLastSeenContactId = contactKey;
        this.ncOfferPromptedContactId = "";
        this.ncOfferPendingChoice = null;
        this.clearNcOfferPendingStorage();
      }
      if (this.ncOfferPromptedContactId === contactKey) return;

      this.ncOfferInFlight = true;
      try {
        const result = await askOfferNotaComercialForContact(contactKey, config.side, (key) =>
          this.$t(key)
        );
        if (result === "error" || result === "feature_off") return;
        this.ncOfferPromptedContactId = contactKey;
        if (result && result.noteId) {
          this.ncOfferPendingChoice = {
            noteId: String(result.noteId),
            amount: Number(result.amount),
            side: config.side,
            contactId: contactKey,
          };
          this.persistNcOfferPending();
          // Solo en el alta se intenta aplicar en el acto: ahí el autosave ya dejó
          // el contacto en la base. En edición el contacto nuevo todavía no está
          // guardado, así que aplicar ahora chocaría contra el anterior; la nota
          // queda pendiente y se aplica cuando el formulario guarda.
          const autoSavedId = this.isOfferAddMode() && this.form && this.form._id;
          if (autoSavedId) this.scheduleApplyPendingNotaComercialOffer(autoSavedId);
        } else {
          this.ncOfferPendingChoice = null;
          this.clearNcOfferPendingStorage();
        }
      } catch (err) {
        console.error("[nota comercial] oferta al elegir contacto:", err);
      } finally {
        this.ncOfferInFlight = false;
      }
    },

    async applyPendingNotaComercialOffer(targetId) {
      const id = targetId != null ? String(targetId) : "";
      if (!id) return;
      if (!this.ncOfferPendingChoice) this.restoreNcOfferPendingFromStorage();
      if (!this.ncOfferPendingChoice) return;
      if (this.ncOfferAppliedTargetId === id) return;

      const choice = { ...this.ncOfferPendingChoice };
      const ok = await applyOfferedNotaComercial(id, choice, (key) => this.$t(key), {
        contactId: choice.contactId,
        maxAttempts: 6,
      });
      if (ok) {
        this.ncOfferAppliedTargetId = id;
        this.ncOfferPendingChoice = null;
        this.clearNcOfferPendingStorage();
      }
    },
  },
};
